// Pestaña "Nota de turno" de Enfermería (2026-09-13). Hasta acá la pestaña
// era solo el formulario: `FormularioSoapie` CREABA el `Composition` y ningún
// componente lo leía, así que la enfermera del turno siguiente no podía ver la
// nota anterior (hallazgo del revisor funcional). Ahora la pestaña es
// formulario + historial, mismo patrón que NotasTab/DocumentosTab (tabla de lo
// ya guardado debajo del picker), y el detalle de cada nota se abre en un
// Dialog de shadcn con sus 6 secciones y, si existe, el PDF firmado.
//
// Sin endpoints nuevos: todo por Medplum desde el cliente, igual que el resto
// de la ficha. El PDF se encuentra por `DocumentReference?related=Composition/<id>`
// — `context.related` lo escribe PanelFirmaImpresion desde este mismo flujo;
// las notas firmadas ANTES de este cambio no lo tienen y muestran "sin PDF".
import type { MedplumClient } from '@medplum/core';
import type { Composition, DocumentReference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { notify } from '@/components/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FormularioDiscretoPage } from '@/formulario/FormularioDiscretoPage';
import { filtroNotasSoapie, leerNotaSoapie, type NotaSoapieLeida } from '@/formulario/discretos/enfermeria';
import { TablaVacia } from './TablaVacia';

function fechaLegible(iso: string | undefined): string {
  return iso ? new Date(iso).toLocaleString('es-EC') : '—';
}

/** El PDF firmado de esta nota, si se firmó después de 2026-09-13 (ver cabecera). `null` = no hay. */
async function buscarPdfDeNota(medplum: MedplumClient, compositionId: string): Promise<DocumentReference | null> {
  try {
    const encontrados = await medplum.searchResources('DocumentReference', {
      related: `Composition/${compositionId}`,
      status: 'current',
      _count: 1,
    });
    return (encontrados as DocumentReference[])[0] ?? null;
  } catch {
    // Una nota sin PDF no es un error que valga interrumpir la lectura.
    return null;
  }
}

function DetalleNota({ nota, onCerrar }: { readonly nota: NotaSoapieLeida | null; readonly onCerrar: () => void }): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [pdf, setPdf] = useState<DocumentReference | null>();
  const [abriendo, setAbriendo] = useState(false);

  useEffect(() => {
    if (!nota?.id) {
      setPdf(undefined);
      return;
    }
    let cancelado = false;
    setPdf(undefined);
    void buscarPdfDeNota(medplum, nota.id).then((d) => !cancelado && setPdf(d));
    return () => {
      cancelado = true;
    };
  }, [medplum, nota?.id]);

  async function verPdf(): Promise<void> {
    const url = pdf?.content?.[0]?.attachment?.url;
    if (!url) return;
    setAbriendo(true);
    try {
      // Mismo camino que DocumentosTab: blob + pestaña nueva, nunca descarga
      // automática (el Binary de Medplum necesita el token de la sesión).
      window.open(URL.createObjectURL(await medplum.download(url)), '_blank');
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
    }
    setAbriendo(false);
  }

  return (
    <Dialog open={nota !== null} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{nota?.titulo ?? t('fichaPaciente.tabs.notaTurno')}</DialogTitle>
        </DialogHeader>
        {nota && (
          <div className="flex flex-col gap-4">
            <p className="font-mono text-xs text-muted-foreground">
              {[fechaLegible(nota.fecha), nota.turno ? `${t('enfermeria.turno')} ${nota.turno}` : '', nota.autor ? `${t('enfermeria.responsable')}: ${nota.autor}` : '']
                .filter(Boolean)
                .join(' · ')}
            </p>
            {nota.secciones.map((s) => (
              <div key={s.clave} className="flex flex-col gap-1">
                <h6 className="text-sm font-semibold">{t(`formularios.fSoapie.${s.clave}`)}</h6>
                <p className="text-sm whitespace-pre-wrap">{s.texto || t('formularios.fSoapie.sinRegistro')}</p>
              </div>
            ))}
            {pdf ? (
              <Button size="sm" variant="outline" className="w-fit" disabled={abriendo} onClick={() => void verPdf()}>
                {t('fichaPaciente.notaTurno.verPdf')}
              </Button>
            ) : (
              pdf === null && <p className="text-xs text-muted-foreground">{t('fichaPaciente.notaTurno.sinPdf')}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HistorialNotasSoapie({ patientId, version }: { readonly patientId: string; readonly version: number }): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [notas, setNotas] = useState<NotaSoapieLeida[]>();
  const [abierta, setAbierta] = useState<NotaSoapieLeida | null>(null);

  useEffect(() => {
    let cancelado = false;
    setNotas(undefined);
    medplum
      .searchResources('Composition', filtroNotasSoapie(patientId))
      .then((r) => !cancelado && setNotas((r as Composition[]).map(leerNotaSoapie)))
      .catch((err: unknown) => {
        if (cancelado) return;
        setNotas([]);
        notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, patientId, version]);

  if (notas === undefined) return <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>;
  if (notas.length === 0) return <TablaVacia mensaje={t('fichaPaciente.notaTurno.sinNotas')} />;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('fichaPaciente.columnas.fecha')}</TableHead>
            <TableHead>{t('enfermeria.diaEnfermeria')}</TableHead>
            <TableHead>{t('enfermeria.turno')}</TableHead>
            <TableHead>{t('enfermeria.responsable')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {notas.map((nota) => (
            <TableRow key={nota.id}>
              <TableCell>
                <button type="button" className="text-sm text-hce-pine underline-offset-2 hover:underline" onClick={() => setAbierta(nota)}>
                  {fechaLegible(nota.fecha)}
                </button>
              </TableCell>
              <TableCell className="font-mono">{nota.dia ?? '—'}</TableCell>
              <TableCell>{nota.turno ?? '—'}</TableCell>
              <TableCell>{nota.autor ?? t('enfermeria.sinResponsable')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <DetalleNota nota={abierta} onCerrar={() => setAbierta(null)} />
    </>
  );
}

export function NotaTurnoTab(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  // Recarga el historial apenas se guarda una nota nueva, sin recargar la
  // página (mismo patrón de `version` que useRespuestasConRefresh en NotasTab).
  const [version, setVersion] = useState(0);

  return (
    <div className="flex flex-col gap-6">
      <FormularioDiscretoPage codigo="enf-soapie" onGuardado={() => setVersion((v) => v + 1)} />
      <section className="flex flex-col gap-2">
        <h5 className="text-sm font-semibold">{t('fichaPaciente.notaTurno.historial')}</h5>
        <HistorialNotasSoapie patientId={id} version={version} />
      </section>
    </div>
  );
}
